"use client";

import * as React from "react";

import api from "../services/api";
import BlogsIndexClient from "./BlogsIndexClient";

type ApiEnvelope<T> = {
  success: boolean;
  data?: T;
  statusCode?: number;
  errorMessage?: string | null;
};

type BlogTag = {
  blogTagId: string;
  blogTagTreeId: string;
  tagTreeName: string;
  parentBlogTagId?: string | null;
  name: string;
  slug: string;
  sortOrder: number;
};

type BlogListItem = {
  blogContentId: string;
  title: string;
  excerpt?: string | null;
  coverImageUrl?: string | null;
  publishedAtUtc: string;
  tags: BlogTag[];
};

type BlogPagedResult<T> = {
  page: number;
  pageSize: number;
  totalCount: number;
  items: T[];
};

type BlogTagNode = {
  blogTagId: string;
  blogTagTreeId: string;
  parentBlogTagId?: string | null;
  name: string;
  slug: string;
  sortOrder: number;
  children: BlogTagNode[];
};

type BlogTagTree = {
  blogTagTreeId: string;
  name: string;
  slug: string;
  sortOrder: number;
  tags: BlogTagNode[];
};

async function getAllBlogs(pageSize = 100) {
  const firstRes = await api.get<ApiEnvelope<BlogPagedResult<BlogListItem>>>(`/Blog?page=1&pageSize=${pageSize}`);
  const first = firstRes.data?.success ? firstRes.data.data : null;
  if (!first) return [];

  const combined = [...(first.items ?? [])];
  const total = first.totalCount ?? combined.length;
  const maxPages = 50;
  let page = 1;

  while (combined.length < total && page < maxPages) {
    page += 1;
    const nextRes = await api.get<ApiEnvelope<BlogPagedResult<BlogListItem>>>(`/Blog?page=${page}&pageSize=${pageSize}`);
    const next = nextRes.data?.success ? nextRes.data.data : null;
    if (!next?.items?.length) break;
    combined.push(...next.items);
  }

  return combined;
}

export default function BlogsPageClient(props: {
  initialBlogs: BlogListItem[];
  initialTagTrees: BlogTagTree[];
  staticBlogContentIds: string[];
}) {
  const { initialBlogs, initialTagTrees, staticBlogContentIds } = props;
  const [blogs, setBlogs] = React.useState<BlogListItem[]>(initialBlogs);
  const [tagTrees, setTagTrees] = React.useState<BlogTagTree[]>(initialTagTrees);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const [blogsData, tagTreesRes] = await Promise.all([
          getAllBlogs(),
          api.get<ApiEnvelope<BlogTagTree[]>>("/Blog/tag-trees"),
        ]);
        const tagTreesData = tagTreesRes.data?.success ? tagTreesRes.data.data ?? [] : [];

        if (!cancelled) {
          setBlogs(blogsData);
          setTagTrees(tagTreesData);
        }
      } catch {
        if (!cancelled) {
          setBlogs([]);
          setTagTrees([]);
          setErrorMessage("Unable to load blogs.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto w-full">
      <BlogsIndexClient
        initialBlogs={blogs}
        tagTrees={tagTrees}
        staticBlogContentIds={staticBlogContentIds}
        isLoading={isLoading}
        errorMessage={errorMessage}
      />
    </div>
  );
}
